// src/hooks/queries/useConversaciones.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conversacionesService } from '@/services/conversaciones';
import { toast } from '@/components/ui';
import type {
  Conversacion,
  ConversacionFiltros,
  ConversacionCreate,
  ConversacionUpdate,
  ConversacionAsignar,
  ConversacionPausar,
  Mensaje,
  MensajeCreate,
} from '@/types';

// === CONVERSACIONES ===

export function useConversaciones(params?: ConversacionFiltros) {
  return useQuery<Conversacion[]>({
    queryKey: ['conversaciones', params],
    queryFn: () => conversacionesService.list(params),
    refetchInterval: 30000, // Refrescar cada 30s
  });
}

export function useConversacion(id: string | undefined) {
  return useQuery<Conversacion>({
    queryKey: ['conversaciones', id],
    queryFn: () => conversacionesService.get(id!),
    enabled: !!id,
  });
}

export function useCrearConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConversacionCreate) => conversacionesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      toast.success('Conversacion creada correctamente');
    },
  });
}

export function useActualizarConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConversacionUpdate }) =>
      conversacionesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', id] });
      toast.success('Conversacion actualizada');
    },
  });
}

export function useAsignarConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConversacionAsignar }) =>
      conversacionesService.asignar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', id] });
      toast.success('Conversacion asignada');
    },
  });
}

export function usePausarConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConversacionPausar }) =>
      conversacionesService.pausar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', id] });
      toast.success('Conversacion pausada');
    },
  });
}

export function useReanudarConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conversacionesService.reanudar(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', id] });
      toast.success('Conversacion reanudada');
    },
  });
}

export function useCerrarConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conversacionesService.cerrar(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', id] });
      toast.success('Conversacion cerrada');
    },
  });
}

// === MENSAJES ===

export function useMensajes(conversacionId: string | undefined) {
  return useQuery<Mensaje[]>({
    queryKey: ['conversaciones', conversacionId, 'mensajes'],
    queryFn: () => conversacionesService.listarMensajes(conversacionId!),
    enabled: !!conversacionId,
    refetchInterval: 5000, // Refrescar cada 5s para simular tiempo real
  });
}

export function useEnviarMensaje() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversacionId, data }: { conversacionId: string; data: MensajeCreate }) =>
      conversacionesService.enviarMensaje(conversacionId, data),
    onSuccess: (_, { conversacionId }) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones', conversacionId, 'mensajes'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
    },
  });
}

export function useMarcarLeidos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversacionId: string) => conversacionesService.marcarLeidos(conversacionId),
    onSuccess: (_, conversacionId) => {
      queryClient.invalidateQueries({ queryKey: ['conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['conversaciones', conversacionId] });
    },
  });
}
